import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Btn, Card, Input, Pill, Select, Toggle } from "../dash/Shell";

const EMPTY = { code: "", type: "percent", value: "", min_amount: "", max_uses: "", expires_at: "" };

export default function AdminCoupons({ onChange }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/coupons").then(({ data }) => setRows(data));
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", {
        code: form.code, type: form.type, value: Number(form.value),
        min_amount: Number(form.min_amount || 0),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
      });
      setForm(EMPTY); await load(); onChange?.(); toast.success("Coupon created");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggle = async (c, active) => { try { await api.patch(`/admin/coupons/${c.id}`, { active }); await load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (c) => { try { await api.delete(`/admin/coupons/${c.id}`); await load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <Card className="lg:col-span-4" title="New coupon" sub="Customers enter this code on the booking page." testid="admin-coupon-form">
        <form onSubmit={create} className="space-y-3">
          <Input data-testid="coupon-code-input" required placeholder="CODE (e.g. OGO10)" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} />
          <div className="grid grid-cols-2 gap-3">
            <Select data-testid="coupon-type-select" value={form.type} onChange={set("type")}><option value="percent">% off</option><option value="flat">₹ flat off</option></Select>
            <Input data-testid="coupon-value-input" required type="number" min="1" placeholder="Value" value={form.value} onChange={set("value")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="coupon-min-input" type="number" min="0" placeholder="Min order ₹" value={form.min_amount} onChange={set("min_amount")} />
            <Input data-testid="coupon-maxuses-input" type="number" min="1" placeholder="Max uses" value={form.max_uses} onChange={set("max_uses")} />
          </div>
          <Input data-testid="coupon-expiry-input" type="date" value={form.expires_at} onChange={set("expires_at")} />
          <Btn data-testid="coupon-create-button" type="submit" className="w-full">Create coupon</Btn>
        </form>
      </Card>

      <Card className="lg:col-span-8" title="Coupons" testid="admin-coupon-list">
        {rows.length === 0 ? <p data-testid="admin-no-coupons" className="text-slate-400 text-sm">No coupons yet.</p> : (
          <ul className="space-y-3">
            {rows.map((c) => (
              <li key={c.id} data-testid={`coupon-row-${c.code}`} className="rounded-2xl border border-white/10 p-4 flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <p className="font-mono text-gold text-lg">{c.code}</p>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}{c.min_amount ? ` · min ₹${c.min_amount}` : ""}{c.max_uses ? ` · ${c.used_count}/${c.max_uses} used` : ` · ${c.used_count} used`}{c.expires_at ? ` · till ${c.expires_at.slice(0, 10)}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Pill tone={c.active ? "mint" : "red"}>{c.active ? "active" : "off"}</Pill>
                  <Toggle testid={`coupon-toggle-${c.code}`} on={c.active} onChange={(v) => toggle(c, v)} />
                  <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c)} className="text-slate-400 hover:text-red-300 p-1"><Trash2 size={16} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
